import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, BookOpen, Plus, Calendar, ChevronDown } from "lucide-react";

export default function MayaManthanJournal() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newEntry, setNewEntry] = useState({
    entry_type: "reflection",
    content: "",
    mood_before: "",
    mood_after: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const entryTypes = [
    { id: "reflection", label: "Daily Reflection" },
    { id: "post_meditation", label: "Post Meditation" },
    { id: "insight", label: "Insight" }
  ];

  const moods = ["calm", "restless", "heavy", "light", "peaceful", "anxious"];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/journal`);
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching journal:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newEntry.content.trim()) {
      toast.error("Please write something");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/maya-manthan/journal`, null, {
        params: {
          entry_type: newEntry.entry_type,
          content: newEntry.content,
          mood_before: newEntry.mood_before || null,
          mood_after: newEntry.mood_after || null
        }
      });
      toast.success("Journal entry saved");
      setNewEntry({ entry_type: "reflection", content: "", mood_before: "", mood_after: "" });
      setShowForm(false);
      fetchEntries();
    } catch (error) {
      toast.error("Failed to save entry");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="maya-journal">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      {/* New Entry Button */}
      <button
        onClick={() => setShowForm(true)}
        className="fixed top-6 right-6 z-50 p-3 rounded-full bg-amber-500 hover:bg-amber-600 text-black transition-colors"
      >
        <Plus className="w-5 h-5" />
      </button>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <BookOpen className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="font-serif text-4xl text-white mb-4">Reflection Journal</h1>
          <p className="text-gray-400">Record your observations, insights, and inner experiences</p>
        </motion.div>

        {/* New Entry Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8"
          >
            <h3 className="text-white font-medium mb-4">New Entry</h3>

            {/* Entry Type */}
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">Type</label>
              <div className="flex flex-wrap gap-2">
                {entryTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setNewEntry(prev => ({ ...prev, entry_type: type.id }))}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      newEntry.entry_type === type.id
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mood Before (for post_meditation) */}
            {newEntry.entry_type === 'post_meditation' && (
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Mood Before</label>
                <div className="flex flex-wrap gap-2">
                  {moods.map(mood => (
                    <button
                      key={mood}
                      onClick={() => setNewEntry(prev => ({ ...prev, mood_before: mood }))}
                      className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                        newEntry.mood_before === mood
                          ? 'bg-indigo-500 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="mb-4">
              <label className="text-gray-400 text-sm mb-2 block">What did you observe?</label>
              <Textarea
                value={newEntry.content}
                onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your reflections here..."
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 min-h-[150px]"
              />
            </div>

            {/* Mood After (for post_meditation) */}
            {newEntry.entry_type === 'post_meditation' && (
              <div className="mb-4">
                <label className="text-gray-400 text-sm mb-2 block">Mood After</label>
                <div className="flex flex-wrap gap-2">
                  {moods.map(mood => (
                    <button
                      key={mood}
                      onClick={() => setNewEntry(prev => ({ ...prev, mood_after: mood }))}
                      className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                        newEntry.mood_after === mood
                          ? 'bg-green-500 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setShowForm(false)}
                variant="ghost"
                className="flex-1 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !newEntry.content.trim()}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
              >
                {submitting ? "Saving..." : "Save Entry"}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Entries List */}
        {entries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <BookOpen className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">No journal entries yet</p>
            <p className="text-gray-600 text-sm">Start writing to capture your inner journey</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry, index) => (
              <motion.div
                key={entry.entry_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                    entry.entry_type === 'reflection' ? 'bg-indigo-500/20 text-indigo-400' :
                    entry.entry_type === 'post_meditation' ? 'bg-green-500/20 text-green-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {entry.entry_type.replace('_', ' ')}
                  </span>
                  <span className="text-gray-600 text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(entry.created_at)}
                  </span>
                </div>

                <p className="text-white leading-relaxed whitespace-pre-wrap">{entry.content}</p>

                {(entry.mood_before || entry.mood_after) && (
                  <div className="mt-4 pt-4 border-t border-white/5 flex gap-4">
                    {entry.mood_before && (
                      <span className="text-xs text-gray-500">
                        Before: <span className="text-gray-400 capitalize">{entry.mood_before}</span>
                      </span>
                    )}
                    {entry.mood_after && (
                      <span className="text-xs text-gray-500">
                        After: <span className="text-gray-400 capitalize">{entry.mood_after}</span>
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
